import {
	getActiveEmergencyWorkOrderCount,
	getApprovalWaitingOnCount,
	getImportantUnreadNotificationCount,
	getStaleWorkOrderCount,
	getWorkOrdersAverageCompletionLag,
	getWorkOrdersCompletedCount,
	getWorkOrdersCompletionRate,
	getWorkOrdersCreatedCount,
} from '../thunks/dashboard_kpi_thunks';
import { nullSafeGet } from '../utils/DataAccessUtils';
import { isBuyerOrInternalTech } from '../utils/AuthUtils';

export const kpiDates = (
	currentUser: any,
	userType: any,
	startDate: moment.Moment,
	endDate: moment.Moment
) => ({
	newWOs: {
		displayName: 'New Work Orders',
		info: (companySettings) => 'Number of new work orders created during this period',
		id: 'newWOs',
		color: 'standard',
		fnToCall: getWorkOrdersCreatedCount(userType)(
			startDate.format('YYYY-MM-DD'),
			endDate.format('YYYY-MM-DD')
		),
		seeDetailsURL: `/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&createdAtStartDate=${encodeURIComponent(
			startDate.format(`MM/DD/YYYY HH:mm:ss`)
		)}&createdAtEndDate=${encodeURIComponent(endDate.format(`MM/DD/YYYY HH:mm:ss`))}`,
	},
	completedWOs: {
		displayName: 'Completed Work Orders',
		info: (companySettings) => 'Number of work orders completed during this period',
		id: 'completedWOs',
		color: 'standard',
		fnToCall: getWorkOrdersCompletedCount(userType)(
			startDate.format('YYYY-MM-DD'),
			endDate.format('YYYY-MM-DD')
		),
		seeDetailsURL: `/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&completedAtStartDate=${encodeURIComponent(
			startDate.format(`MM/DD/YYYY HH:mm:ss`)
		)}&completedAtEndDate=${encodeURIComponent(endDate.format(`MM/DD/YYYY HH:mm:ss`))}`,
	},
	completionRateWO: {
		displayName: 'Work Order Completion Rate',
		info: (companySettings) =>
			'Percentage of work orders completed relative to the new work orders created during this period (Completed Work Orders / New Work Orders)',
		id: 'completionRateWO',
		color: 'standard',
		type: 'rate',
		fnToCall: getWorkOrdersCompletionRate(userType)(
			startDate.format('YYYY-MM-DD'),
			endDate.format('YYYY-MM-DD')
		),
		seeDetailsURL: `/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&completedAtStartDate=${encodeURIComponent(
			startDate.format(`MM/DD/YYYY HH:mm:ss`)
		)}&completedAtEndDate=${encodeURIComponent(endDate.format(`MM/DD/YYYY HH:mm:ss`))}`,
	},
	completionLagWO: {
		displayName: 'Work Order Completion Time',
		info: (companySettings) => 'Average duration taken to complete work orders',
		id: 'completionLagWO',
		color: 'reverse',
		type: 'time',
		fnToCall: getWorkOrdersAverageCompletionLag(userType)(
			startDate.format('YYYY-MM-DD'),
			endDate.format('YYYY-MM-DD')
		),
		seeDetailsURL: `/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&completedAtStartDate=${encodeURIComponent(
			startDate.format(`MM/DD/YYYY HH:mm:ss`)
		)}&completedAtEndDate=${encodeURIComponent(endDate.format(`MM/DD/YYYY HH:mm:ss`))}`,
	},
});
export const kpis = (currentUser, userType) => ({
	importantUnreadNotifications: {
		displayName: 'Unread Important Notifications',
		info: (companySettings) => 'Number of unread critical notifications require your attention',
		id: 'importantUnreadNotifications',
		color: 'standard',
		fnToCall: getImportantUnreadNotificationCount(userType),
		seeDetailsURL: `/${userType}/inbox/overview/important`,
	},
	...(isBuyerOrInternalTech(currentUser)
		? {
				waitingOnMyApproval: {
					displayName: 'Waiting On Your Approval',
					info: (companySettings) =>
						'Number of work orders/quotes/invoices waiting on your approval',
					id: 'waitingOnMyApproval',
					color: 'standard',
					fnToCall: getApprovalWaitingOnCount(userType),
					seeDetailsURL: `/${userType}/inbox/overview/approvals`,
				},
		  }
		: {}),
	emergencyWOs: {
		displayName: 'Active Emergency Work Orders',
		info: (companySettings) => 'Number of emergency work orders that are not completed/cancelled',
		id: 'emergencyWOs',
		color: 'standard',
		valueName: 'count',
		fnToCall: getActiveEmergencyWorkOrderCount(userType),
		seeDetailsURL: `/${userType}/workOrders/overview/active?current=1&pageSize=5&sort_by=createdAt&order=desc&hasBuyerArchived=false&displayStatuses=Open%2COnHold%2CInProgress`,
	},
	staleWOs: {
		displayName: 'Stale Work Orders',
		info: (companySettings) =>
			`Number of work orders that are past due and stale ${
				isBuyerOrInternalTech(currentUser)
					? `(${nullSafeGet(
							'config.workOrder.stale.afterXDays',
							companySettings
					  )} days past supplier/technician activity on the work order)`
					: ``
			}`,
		id: 'staleWOs',
		color: 'standard',
		valueName: 'count',
		fnToCall: getStaleWorkOrderCount(userType),
		seeDetailsURL: `/${userType}/workOrders/overview/active?current=1&pageSize=5&sort_by=createdAt&order=desc&isStale=true`,
	},
});
